import type { AccountDevice } from '../transport/api'

/**
 * WHICH AGENTS ARE STUCK WAITING FOR A PERSON, and where to go about it.
 *
 * A cross-machine fork into a NEW folder lands the agent on Claude's "do you trust
 * the files in this folder?" prompt. It is alive, it has the conversation, and what a
 * person sees is "the fork worked, and then it ignored me". Four of us with the
 * source, the server and the device list spent hours finding that on 2026-08-22. On
 * production nobody has any of those.
 *
 * WHAT THIS COSTS, third answer and this one is read from the RULE rather than from
 * somebody watching. `bff/agents.py:63` — `HOLD_CEILING_S = 6 * 3600`:
 *
 *   cleared inside six hours  → the message lands, just late
 *   still blocked after six   → it passes the ceiling and stops being deliverable
 *
 * The first two versions of this line came from descriptions I did not check — "held"
 * (from "it never answers"), then "delayed" (from "it answered after all"). Each was a
 * segment of the rule mistaken for the whole of it, and BOTH were wrong in the
 * direction of whoever had just looked. So this one names the bound instead of the
 * outcome: a person needs to know there is a deadline, not a theory about the queue.
 *
 * The ceiling is not cleanup. agents.py says so itself: "it is the point at which the
 * system regains the ability to say WHY something did not arrive." That ability was
 * built — `AgentQueue.overdue()` at agents.py:619 — and wired to nothing: no route
 * serves it and this app has never heard the word. Worth connecting, and not today.
 *
 * The fact was already reaching the cloud (`/account/devices` → `agents[].blocked`);
 * nothing drew it. This is the drawing.
 *
 * A GENERAL DETECTOR, NOT A FIX FOR ONE CAUSE. The folder prompt is one way to end up
 * at `at_prompt`; a menu or a confirmation is another. avg-latency put it best: fixing
 * one instance and skipping the detector is exactly how the next instance becomes
 * invisible.
 *
 * `blocked` is read from `/account/devices` rather than from the idle roster the rail
 * already polls, because the roster carries a BOOLEAN (`needs_input`) and this carries
 * the REASON. "Waiting for you to answer something" and "its user is typing" want
 * different words and different reactions — the queue sheet already refuses to fold
 * them together, and so does this.
 */

/** Reasons the cloud reports. `working` is ordinary busy, not stuck. */
export type BlockedReason = 'at_prompt' | 'typing' | 'speaking' | 'working'

/** Keyed `device_id session` — two machines can run the same session name. */
export function blockedByAgent(devices: AccountDevice[] | undefined): Map<string, BlockedReason> {
  const out = new Map<string, BlockedReason>()
  for (const d of devices ?? []) {
    // `agents` used to be reached through a cast because nothing declared it; it is
    // `AccountAgent[]` now (relay.ts), so the shape is checked rather than asserted.
    for (const a of d.agents ?? []) {
      if (!a.session || !a.blocked) continue
      out.set(blockedKey(d.device_id, a.session), a.blocked as BlockedReason)
    }
  }
  return out
}

export function blockedKey(device: string | null | undefined, session: string): string {
  return `${device ?? ''} ${session}`
}

/**
 * What to say, and whether it is worth saying.
 *
 * Only `at_prompt` earns a mark. `working` is ordinary busy and the rail already draws
 * that as a halo — a second indicator for the same state is noise, and noise is what
 * makes people stop reading the one that matters. `typing`/`speaking` mean a PERSON is
 * mid-input at that machine; the message will land on its own, so there is nothing to
 * go and do.
 */
export function blockedNotice(
  reason: BlockedReason | undefined,
  machine: string,
): { short: string; long: string } | null {
  if (reason !== 'at_prompt') return null
  return {
    short: 'answer',
    /*
     * Names the machine (the only way to clear it is to go there) and the BOUND, not
     * the outcome. "Held" and "delayed" were both tried and both promised something
     * the rule does not: one that it stays, one that it arrives. "Expire" is true on
     * either side of the six-hour ceiling, and it is the half that makes a person act.
     */
    long: `Waiting for someone to answer a prompt on ${machine} — messages sent to it wait for that, and expire after six hours`,
  }
}
